/* ── Publish-on-your-website: pure logic ────────────────────────────────
   Spec: docs/product specs/WEBSITE_EMBED_SPEC.md (v6)
   Design: docs/product specs/design_handoff_publish_modal/

   Everything here is pure and synchronous so it can be tested without a
   backend or a DOM — same split as guidedRoomFlow / leadGateFlow.

   ── THE MODEL, IN ONE PARAGRAPH ──
   A project has ASSETS (meta models + digital twins) and SITES (websites the
   customer registered). A PLACEMENT is one (asset, site) pair and owns its own
   opaque embed key — DT1 on two sites is TWO keys and two snippets (spec
   §7.1.1). The lead gate belongs to the SITE, not the asset, so every asset on
   one site shares one Open/Closed toggle (§11.2), and the unlock a visitor
   earns is scoped to (project, site) — intra-site, never inter-site (§11.3).

   ── WHAT ISN'T HERE ──
   No route views: a route view has no lead gate anywhere in the app, so it
   cannot honour the site toggle (§4). Meta models and digital twins only.
*/

export type AssetKind = "MM" | "DT";

export interface EmbedAsset {
  id: string;
  kind: AssetKind;
  /** Display code — MM1, DT2. Positional within its kind. */
  code: string;
  name: string;
  /** Secondary line under the name; free text, may be empty. */
  sub: string;
}

export interface EmbedSite {
  id: string;
  /** Display code — S1, S2. Positional. */
  code: string;
  /** Stored apex form: no scheme, no leading www., no path. */
  domain: string;
  label: string;
  /** Lead gate: true = Open (gate arms), false = Closed. */
  lead: boolean;
  /** Include all subdomains — opt-in, never implied (§7.2.1). */
  subs: boolean;
  /** The website's master switch as STORED. null = nobody has chosen; infer from the ticks. */
  embedsEnabled?: boolean | null;
}

export interface Placement {
  /** Opaque per-pair key. Survives unpublish/republish — never regenerate. */
  key: string;
  /** The tick. false = placed but not rendering (§10.1.1). */
  published: boolean;
  /** True until a real server key replaces the optimistic local one. */
  local?: boolean;
  /**
   * What the first tap does. Undefined = never chosen, so the default applies.
   *
   * PER PLACEMENT, not per website, unlike the lead gate. The gate is per-site
   * because a visitor should be asked once per site — a rule about the person.
   * This is a rule about the PAGE, and one site routinely holds both: a hero
   * walkthrough at the top of a landing page, and a small card further down.
   */
  openMode?: OpenMode;
}

/**
 * Fullscreen on the first tap, or play inside the page.
 *
 * Neither is right everywhere. On a campaign microsite the walkthrough IS the
 * page, and taking the screen is the product. On a landing page carrying specs,
 * pricing and an enquiry form, seizing the screen ejects the visitor from the
 * page the developer paid to build.
 *
 * Either way the ⤢ control stays in the player, so this only ever decides the
 * FIRST tap — the visitor keeps both modes regardless.
 */
export type OpenMode = "fullscreen" | "inline";

const OPEN_MODES: OpenMode[] = ["fullscreen", "inline"];

export const isOpenMode = (v: any): v is OpenMode => OPEN_MODES.indexOf(v) >= 0;

/** placements[siteId][assetId] */
export type Placements = Record<string, Record<string, Placement>>;

export const KIND_WORD: Record<AssetKind, string> = {
  MM: "Meta model",
  DT: "Digital twin",
};

/* ── Host / URL ──────────────────────────────────────────────────────── */

/** Embed host per spec §6 — NEVER the main app host, which must stay unframeable. */
export const embedHost = (): string => {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  // Local dev and any *.stage host point at the stage embed host; everything
  // else is prod. Mirrors the `visit.` / `my.` host derivation.
  const isStage =
    /(^|\.)stage\./.test(host) ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".netlify.app");
  return isStage
    ? "https://embed.stage.openskyvr.com"
    : "https://embed.openskyvr.com";
};

export const embedUrl = (key: string): string => `${embedHost()}/e/${key}`;

const KEY_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

/**
 * Mint an opaque key — `emb_` + 22 base62 chars (~131 bits), per §7.1.2.
 *
 * Used only as the OPTIMISTIC placeholder while the create call is in flight;
 * the server's key replaces it on success. Deliberately carries no structure:
 * no asset id, no site id, no shared prefix between siblings, so holding one
 * pair's code gives no purchase on another's.
 */
export const mintLocalKey = (): string => {
  let out = "";
  const n = 22;
  const cryptoObj: Crypto | undefined =
    typeof crypto !== "undefined" ? crypto : undefined;
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint8Array(n);
    cryptoObj.getRandomValues(buf);
    for (let i = 0; i < n; i += 1) out += KEY_ALPHABET[buf[i] % 62];
  } else {
    for (let i = 0; i < n; i += 1) {
      out += KEY_ALPHABET[Math.floor(Math.random() * 62)];
    }
  }
  return `emb_${out}`;
};

/* ── Domain normalisation (§7.2.2) ───────────────────────────────────── */

/**
 * Accept what people paste, strip it to a stored apex form.
 *
 * Handles (all listed in §7.2.2): mixed case, http/https, scheme-relative
 * `//host`, path / query / hash, port, whitespace, a trailing FQDN dot, and a
 * leading `www.` — the pair is derived on expansion, so only one form is
 * stored. Returns "" when nothing host-shaped remains.
 */
export const normalizeDomain = (raw: string): string => {
  let v = String(raw || "").trim().toLowerCase();
  if (!v) return "";
  v = v.replace(/^[a-z][a-z0-9+.-]*:\/\//, ""); // scheme
  v = v.replace(/^\/\//, ""); // scheme-relative
  v = v.replace(/^[^/?#]*@/, ""); // userinfo, if someone pastes a URL with one
  v = v.split("/")[0].split("?")[0].split("#")[0];
  v = v.replace(/:\d+$/, ""); // port
  v = v.replace(/\.+$/, ""); // trailing FQDN dot
  const wildcard = v.startsWith("*.");
  if (wildcard) v = v.slice(2);
  v = v.replace(/^www\./, ""); // stored apex form; www. is paired on expansion
  return wildcard ? `*.${v}` : v;
};

/* ── Rejection rules (§7.2.3) ────────────────────────────────────────── */

/**
 * Multi-part public suffixes. Not a full PSL — just enough that `*.co.in`
 * and friends are refused. A miss here fails SAFE: the domain is accepted as
 * an ordinary host, which is the same outcome as today.
 */
const MULTI_SUFFIXES = [
  "co.in", "net.in", "org.in", "gen.in", "firm.in", "ind.in", "ac.in", "gov.in",
  "co.uk", "org.uk", "me.uk", "ac.uk", "gov.uk",
  "com.au", "net.au", "org.au", "co.nz", "co.za", "co.jp", "or.jp", "ne.jp",
  "com.br", "com.sg", "com.my", "com.hk", "com.tw", "com.cn", "com.mx",
  "com.ar", "com.tr", "com.sa", "com.ph", "com.pk", "com.bd", "com.np",
];

/**
 * Shared hosting platforms. A WILDCARD across one of these would let every
 * other tenant on the platform frame the embed — realistic for small builders
 * on Wix or Netlify. An exact host on them (`ruchira.wixsite.com`) is fine.
 */
const SHARED_PLATFORMS = [
  "netlify.app", "vercel.app", "wixsite.com", "editorx.io", "github.io",
  "web.app", "firebaseapp.com", "pages.dev", "herokuapp.com", "glitch.me",
  "surge.sh", "squarespace.com", "weebly.com", "blogspot.com", "wordpress.com",
  "myshopify.com", "azurewebsites.net", "cloudfront.net", "ngrok.io",
  "ngrok-free.app", "onrender.com", "replit.app", "framer.website",
  "carrd.co", "webflow.io", "bubbleapps.io", "godaddysites.com",
];

const LABEL_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export interface DomainCheck {
  ok: boolean;
  /** Normalised apex on success. */
  host: string;
  /** True when the input carried a `*.` prefix (kept only to explain the error). */
  wildcard?: boolean;
  error?: string;
}

export interface DomainCheckOptions {
  /** Existing apexes, to reject duplicates. */
  existing?: string[];
  /** Stage-only escape hatch for local testing. */
  allowLocalhost?: boolean;
}

/**
 * Validate an entered domain. Runs the same rules the backend must enforce
 * (§7.2.3) — this copy exists for the inline error message, not as the
 * enforcement point.
 */
/**
 * Does this newly typed host look like a SLIP of one they already have?
 *
 * There is a live key on stage bound to `opesnkyvr.com` — "openskyvr" with two letters
 * transposed. It validated fine, a key was minted, and the embed silently never worked:
 * nothing is syntactically wrong with a typo, and we cannot know how they spell their own
 * domain. What we CAN see is that it is one edit away from a domain they already publish on,
 * which is worth a question rather than a rejection.
 *
 * Returns the host it resembles, or "". Damerau-Levenshtein, so a transposition costs 1 —
 * which is exactly the case that produced `opesnkyvr.com`.
 */
export const looksLikeTypoOf = (host: string, existing: string[]): string => {
  const a = String(host || "").toLowerCase();
  if (!a) return "";
  for (const raw of existing) {
    const b = String(raw || "").toLowerCase();
    if (!b || b === a) continue;
    // Only compare the registrable-ish part: a.com vs a.in is a DIFFERENT site, not a typo.
    if (a.split(".").slice(1).join(".") !== b.split(".").slice(1).join(".")) continue;
    const d = editDistance(a.split(".")[0], b.split(".")[0]);
    // 1 edit on a short label is usually a typo; on a long one, 1–2 still is. Never 0 — that
    // is the duplicate check's job — and never so loose that "villas" flags "villa".
    if (d > 0 && d <= (a.length > 12 ? 2 : 1)) return b;
  }
  return "";
};

/** Damerau-Levenshtein: insert, delete, substitute, or TRANSPOSE, each costing 1. */
const editDistance = (a: string, b: string): number => {
  const m = a.length, n = b.length;
  const d: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);   // the transposition case
      }
    }
  }
  return d[m][n];
};

export const checkDomain = (
  raw: string,
  opts: DomainCheckOptions = {},
): DomainCheck => {
  const existing = (opts.existing || []).map((d) => d.toLowerCase());
  const norm = normalizeDomain(raw);
  if (!norm) return { ok: false, host: "", error: "Enter a domain." };

  const wildcard = norm.startsWith("*.");
  const bare = wildcard ? norm.slice(2) : norm;

  if (bare.includes("*")) {
    return {
      ok: false, host: "", wildcard,
      error: "Only one leading “*.” is supported — not *.*. or www.*.",
    };
  }
  if (bare.includes(":")) {
    return { ok: false, host: "", error: "That looks like an IP address, not a domain." };
  }
  if (!bare.includes(".")) {
    if (
      opts.allowLocalhost &&
      (bare === "localhost" || bare === "127.0.0.1")
    ) {
      return { ok: true, host: bare };
    }
    // `*.com` — a single label behind a wildcard. Rejected either way, but the
    // reason matters: told "that doesn't look like a domain", someone retries
    // with `*.co.uk` and gets a different answer. Name the real objection.
    if (wildcard) {
      return {
        ok: false, host: "", wildcard,
        error: `“*.${bare}” is far too broad — that’s every site on ${bare}.`,
      };
    }
    return {
      ok: false, host: "",
      error: "That doesn’t look like a domain — try ruchiraprojects.com",
    };
  }
  if (bare === "localhost" || bare === "127.0.0.1" || /^\d{1,3}(\.\d{1,3}){3}$/.test(bare)) {
    if (opts.allowLocalhost) return { ok: true, host: bare };
    return { ok: false, host: "", error: "IP addresses can’t be used — enter a domain name." };
  }

  const labels = bare.split(".");
  if (labels.some((l) => !LABEL_RE.test(l))) {
    return {
      ok: false, host: "",
      error: "Letters, numbers and hyphens only, and no empty parts.",
    };
  }

  // A wildcard must not sit directly on a public suffix — `*.com`, `*.co.in`.
  if (wildcard) {
    const isSuffixOnly =
      labels.length < 2 ||
      (labels.length === 2 && MULTI_SUFFIXES.includes(bare));
    if (isSuffixOnly) {
      return {
        ok: false, host: "", wildcard,
        error: `“*.${bare}” is far too broad — that’s every site on ${bare}.`,
      };
    }
    const platform = SHARED_PLATFORMS.find((p) => bare === p);
    if (platform) {
      return {
        ok: false, host: "", wildcard,
        error: `“*.${platform}” would let any other site on ${platform} show your walkthrough.`,
      };
    }
  }

  if (existing.includes(bare)) {
    return { ok: false, host: bare, error: `${bare} is already on the list.` };
  }
  // A wildcard entry is expressed by the site's "include all subdomains"
  // checkbox, not by typing `*.` — collapse it to the apex and let the flag
  // carry the meaning.
  return { ok: true, host: bare, wildcard };
};

/* ── Host expansion (§7.2.1) ─────────────────────────────────────────── */

export interface ExpandedHost {
  host: string;
  /** Right-aligned note in the design's hosts block. */
  note: string;
  /** false renders dimmed with a grey tick. */
  included: boolean;
}

/**
 * One SITE expands to a set of hosts. `www.` is paired silently and always;
 * the subdomain wildcard only when the site's `subs` flag is on.
 *
 * The pairing deliberately does NOT compute the registrable domain — it strips
 * or adds a single leading `www.`. Naive apex-derivation would mangle
 * `www.ruchiraprojects.co.in` into `co.in`.
 */
export const expandHosts = (domain: string, subs: boolean): ExpandedHost[] => {
  const apex = String(domain || "").replace(/^www\./, "");
  return [
    { host: apex, note: "apex", included: true },
    { host: `www.${apex}`, note: "paired automatically", included: true },
    {
      host: `*.${apex}`,
      note: subs ? "all subdomains" : "not included",
      included: !!subs,
    },
  ];
};

/**
 * The `frame-ancestors` list the Edge Function emits for a key — https only,
 * and scoped to THIS key's site, never the whole project allowlist (§5.4).
 *
 * https-only is deliberate: a scheme-less host-source matches only the
 * protected document's scheme, and an http parent page can be tampered with in
 * transit while a lead gate sits inside it.
 */
export const frameAncestorsFor = (domain: string, subs: boolean): string[] =>
  expandHosts(domain, subs)
    .filter((h) => h.included)
    .map((h) => `https://${h.host}`);

/* ── The snippet ─────────────────────────────────────────────────────── */

export interface SnippetInput {
  key: string;
  assetName: string;
  projectName: string;
}

/**
 * The iframe src for a snippet. THE KEY, AND NOTHING ELSE.
 *
 * No settings ride in this URL, and none ever should. The whole model rests on
 * the pasted code never changing: publishing, unpublishing and the lead gate are
 * all remote precisely so a builder never has to go back to their web team —
 * who are often a third-party agency on a retainer — to change something in the
 * dashboard.
 *
 * `open_mode` briefly broke that. It was written in as `?fs=0` so the setting
 * would work before the backend field existed, which meant switching fullscreen
 * off changed the snippet under a customer who had already pasted it: the modal
 * showed one code, their live page ran another, and nothing said so. A setting
 * that silently stops matching the live site is worse than a setting that is
 * not there yet. It comes from the render payload now, like everything else.
 */
export const snippetSrc = ({ key }: SnippetInput): string => embedUrl(key);

/**
 * `allow` carries the motion permissions as well as fullscreen, and the SAME
 * list must be on our inner iframe too — `allow` does not inherit downward
 * (§10.5). The design's snippet omitted accelerometer/gyroscope; this doesn't.
 */
export const ALLOW_LIST = "fullscreen; xr-spatial-tracking; accelerometer; gyroscope";

/**
 * The <script> is not decoration either: an iframe cannot read its parent's URL, so without
 * it a visitor who arrived from an ad reaches us stripped of their campaign tags and the CRM
 * files every walkthrough lead as organic. It reads five utm_* values off the host page and
 * appends them to our src. It touches nothing else on the page, and an embed pasted without
 * it still works — it just loses paid attribution.
 *
 * `min-height` is doing real work here, not padding the markup.
 *
 * 16:9 of a phone-width column is about 195px tall, and that is the viewport
 * the embedded page actually gets — the walkthrough is a letterbox slit and the
 * lead gate has nowhere to stand. `min(70vh,420px)` floors it at something
 * usable without touching desktop, where 16:9 of a wide column already exceeds
 * 420px and the floor never binds. One declaration, no media query, and it
 * degrades to plain 16:9 in anything too old to know `min()`.
 */
export const snippetText = (input: SnippetInput): string => {
  const { key, assetName, projectName } = input;
  const title = [assetName, projectName].filter(Boolean).join(" · ");
  const id = `opensky-${key.slice(4, 14)}`;   // stable per placement, and not the key itself
  return `<div style="position:relative;width:100%;aspect-ratio:16/9;min-height:min(70vh,420px)">
  <iframe
    id="${id}"
    src="${snippetSrc(input)}"
    style="position:absolute;inset:0;width:100%;height:100%;border:0"
    title="${title.replace(/"/g, "&quot;")}"
    allow="${ALLOW_LIST}"
    allowfullscreen></iframe>
</div>
<script>
/* Passes this page's campaign tags to the walkthrough, so a lead from a paid visit is
   reported under the campaign that paid for it. Runs once, changes nothing else. */
(function () {
  var f = document.getElementById("${id}");
  if (!f) return;
  var q = new URLSearchParams(window.location.search), out = [];
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach(function (k) {
    var v = q.get(k);
    if (v) out.push(k + "=" + encodeURIComponent(v.slice(0, 200)));
  });
  if (out.length) f.src += (f.src.indexOf("?") < 0 ? "?" : "&") + out.join("&");
})();
</script>`;
};

export type SnippetTokenKind = "tag" | "attr" | "val" | "plain";
export interface SnippetToken { k: SnippetTokenKind; s: string }

/**
 * THE REACT / NEXT.JS VERSION — a genuinely different snippet, not a reformat.
 *
 * Pasting the HTML block into JSX fails in three separate ways, none of which
 * throws an error the web team would notice until the embed is already live:
 *   1. `style="…"` must be an object in JSX, and a string silently throws.
 *   2. `allowfullscreen` and `class` are not JSX attribute names.
 *   3. A `<script>` tag written inside JSX is NEVER executed by React. The
 *      walkthrough would still render, so nothing looks broken — the campaign
 *      tags just stop arriving and every paid lead files as organic.
 *
 * So the UTM pass moves into an effect, and the effect rebuilds the URL from a
 * CONSTANT base each time rather than appending to the live `src`. That makes
 * it idempotent under StrictMode's double-invoke (which would otherwise append
 * the tags twice) and safe under SSR, where the first paint must match the
 * server's — hence state set after mount rather than a `useMemo`.
 */
export const snippetReact = (input: SnippetInput): string => {
  const { assetName, projectName } = input;
  const title = [assetName, projectName].filter(Boolean).join(" · ");
  return `// Next.js App Router: add "use client" as the very first line of this file.
import { useEffect, useState } from "react";

const EMBED_SRC = "${snippetSrc(input)}";

export default function ImmersiveWalkthrough() {
  const [src, setSrc] = useState(EMBED_SRC);

  // Passes this page's campaign tags to the walkthrough, so a lead from a paid
  // visit is reported under the campaign that paid for it. Always rebuilt from
  // EMBED_SRC, so running twice cannot double up the tags.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const out = [];
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
      const v = q.get(k);
      if (v) out.push(k + "=" + encodeURIComponent(v.slice(0, 200)));
    });
    if (out.length) setSrc(EMBED_SRC + "?" + out.join("&"));
  }, []);

  return (
    <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", minHeight: "min(70vh, 420px)" }}>
      <iframe
        src={src}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        title="${title.replace(/"/g, "&quot;")}"
        allow="${ALLOW_LIST}"
        allowFullScreen
      />
    </div>
  );
}`;
};

/**
 * Resolve how the embed opens: the key's `open_mode`, else the default.
 *
 * `?fs=` is a TESTING HATCH, nothing more. We never write it into a snippet —
 * see `snippetSrc` — and it loses to the dashboard whenever the dashboard has an
 * opinion, so it can never quietly override what a customer set. It exists so
 * the behaviour can be exercised on a real page before the backend field ships,
 * and so support can reproduce a customer's report by hand.
 *
 * `open_mode` must stay ABSENT on a key nobody has configured. The day it starts
 * defaulting to "fullscreen" server-side is the day it starts overriding this
 * hatch for everyone using it.
 *
 * Reading the param loosely (`0`, `no`, `false`, `inline`) costs nothing and
 * saves a round trip when someone types the obvious synonym.
 */
export const resolveOpenMode = (fromKey: unknown, search = ""): OpenMode => {
  if (isOpenMode(fromKey)) return fromKey;
  let raw = "";
  try {
    raw = (new URLSearchParams(search).get("fs") || "").trim().toLowerCase();
  } catch {
    /* no URLSearchParams — fall through to the default */
  }
  if (!raw) return "fullscreen";
  if (["0", "no", "false", "off", "inline"].indexOf(raw) >= 0) return "inline";
  return "fullscreen";
};

/**
 * What the visitor's device is, as far as this decision cares.
 *
 * Read from `window` at the call site rather than in here, so the rule below
 * stays pure and testable — the bug this guards against only ever appears on
 * hardware nobody has on their desk.
 */
export interface DeviceHints {
  /** `(pointer: coarse)` — a finger, not a mouse. */
  coarsePointer: boolean;
  /** The SCREEN's short edge, not the iframe's. A phone is narrow either way up. */
  minScreenPx: number;
}

/**
 * A phone — the one device where "stays in the page" has nothing to offer.
 *
 * 560px on the short edge: comfortably above every phone in landscape and below
 * every tablet in portrait (an iPad mini is 744). A tablet is deliberately NOT a
 * phone here — at 70vh an embedded walkthrough on a tablet is perfectly usable,
 * and a customer who chose inline for a page full of specs meant it there.
 *
 * Coarse pointer AND small screen, because either alone is wrong: a touchscreen
 * laptop is coarse and huge, and a small browser window on a desktop is narrow
 * but driven by a mouse.
 */
export const isPhone = (d: DeviceHints): boolean =>
  d.coarsePointer && d.minScreenPx > 0 && d.minScreenPx <= 560;

/**
 * Does the first tap go fullscreen?
 *
 * The switch decides on desktop. A PHONE ALWAYS GOES FULLSCREEN, whatever the
 * setting says — the only reason to keep a walkthrough in the page is that
 * something sits beside it, and on a phone nothing does. Everything is one
 * stacked column, so inline buys the customer nothing and costs the visitor a
 * 16:9 slit of a walkthrough.
 *
 * "Goes" is still the browser's call: iOS Safari grants element fullscreen to
 * video only, so on an iPhone this returns true and nothing happens. The tour
 * plays at the snippet's `min-height` floor instead. Enhancement, not dependency.
 */
export const opensFullscreen = (mode: OpenMode, device: DeviceHints): boolean =>
  mode === "fullscreen" || isPhone(device);

/** Which version of the code a platform needs. Only React differs. */
export type SnippetFlavor = "html" | "react";

export const snippetFor = (input: SnippetInput, flavor: SnippetFlavor = "html"): string =>
  flavor === "react" ? snippetReact(input) : snippetText(input);

/**
 * Tokenise the snippet for the design's three code colours. Done here rather
 * than with `dangerouslySetInnerHTML` in the component: the project name and
 * asset name are user-entered, so they must never be interpolated into markup.
 *
 * Two grammars, because the two snippets are two languages. Colouring JSX with
 * the HTML pass turns every `useEffect` into a tag name, which is worse than no
 * colour at all.
 */
export const snippetTokens = (
  input: SnippetInput,
  flavor: SnippetFlavor = "html",
): SnippetToken[] => {
  const text = snippetFor(input, flavor);
  const out: SnippetToken[] = [];
  const push = (k: SnippetTokenKind, s: string) => { if (s) out.push({ k, s }); };
  // HTML: a tag boundary, or attr="value", or a bare attribute.
  // JSX:  a comment, or a string, or a JS keyword, or a component/tag name.
  const re = flavor === "react"
    ? /(\/\/[^\n]*)|("[^"\n]*")|\b(import|from|export|default|function|const|let|return|forEach|if)\b|(<\/?[A-Za-z]+|\/?>)/g
    : /(<\/?[a-z]+|\/?>)|([a-z-]+)(=)("[^"]*")|([a-z]+)(?=[\s>])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(text))) {
    if (m.index > last) push("plain", text.slice(last, m.index));
    if (flavor === "react") {
      if (m[1]) push("plain", m[1]);        // comments stay the muted base colour
      else if (m[2]) push("val", m[2]);
      else if (m[3]) push("attr", m[3]);
      else if (m[4]) push("tag", m[4]);
    } else if (m[1]) push("tag", m[1]);
    else if (m[2]) { push("attr", m[2]); push("plain", m[3]); push("val", m[4]); }
    else if (m[5]) push("attr", m[5]);
    last = m.index + m[0].length;
  }
  if (last < text.length) push("plain", text.slice(last));
  return out;
};

/* ── Where the code goes, per platform ──────────────────────────────────
   The modal used to hand over a block of markup and say nothing about where it
   belongs. Every customer's web team is on a different stack, and the two
   questions they always ask first are "where do I paste this" and "is my copy
   the same as everyone else's". Both are answered here rather than over email.

   `sameCode: true` is stated out loud on each tab precisely BECAUSE it is the
   boring answer — silence reads as "there might be a different one and nobody
   told me", and a web team that suspects that will ask before shipping.

   `caution` is for the one thing per platform that silently costs attribution
   rather than breaking the render, because that is the failure nobody reports:
   the walkthrough is on the page, so it looks done. */
export interface EmbedPlatform {
  id: string;
  label: string;
  /** false = this platform needs its own version of the code. */
  sameCode: boolean;
  flavor: SnippetFlavor;
  /** One line under the tabs, before the steps. */
  lede: string;
  steps: string[];
  caution?: string;
}

export const EMBED_PLATFORMS: EmbedPlatform[] = [
  {
    id: "html",
    label: "HTML",
    sameCode: true,
    flavor: "html",
    lede: "Any site whose page HTML you can edit — hand-built, a template, or a CMS with a code block.",
    steps: [
      "Open the page where the walkthrough should appear.",
      "Paste the block exactly where you want it to sit. It fills the width of whatever column it is in and sets its own height, so no sizing is needed.",
      "Save and publish the page. Nothing to install — no account, no library, no key to configure.",
    ],
  },
  {
    id: "wordpress",
    label: "WordPress",
    sameCode: true,
    flavor: "html",
    lede: "Same code as the HTML tab, pasted into a code block rather than the visual editor.",
    steps: [
      "Edit the page, then add a Custom HTML block (block editor) — or, in the classic editor, switch from Visual to the Text tab.",
      "Paste the block and click Update. The visual editor will not do: it escapes the tags and the code appears on the page as text.",
      "Using a page builder? Elementor has an HTML widget, Divi a Code module, WPBakery a Raw HTML element. Any of those works.",
    ],
    caution: "Some security plugins, and editor roles below Administrator, strip <script> tags on save. If the walkthrough appears but campaign tags stop reaching your leads, that is what happened — the embed itself keeps working, you only lose paid-campaign attribution.",
  },
  {
    id: "builders",
    label: "Wix · Squarespace · Webflow · Shopify",
    sameCode: true,
    flavor: "html",
    lede: "Same code as the HTML tab. Every one of these has an embed block that takes raw HTML.",
    steps: [
      "Wix: Add → Embed Code → Embed HTML → Code, then drag the box to the width you want. Wix does not resize it for you.",
      "Squarespace: add a Code block, leave it set to HTML, paste. Code blocks need a Business plan or above.",
      "Webflow: drag in an Embed component, paste, then publish the site.",
      "Shopify: add a Custom Liquid section to the page's template and paste there.",
    ],
    caution: "Wix renders custom code inside its own sandboxed frame on a Wix-owned address, not on your domain. If the walkthrough shows a “not authorised” notice there, send us a screenshot — it is one line for us to allow, and nothing changes on your side.",
  },
  {
    id: "react",
    label: "React · Next.js",
    sameCode: false,
    flavor: "react",
    lede: "This one is different. The HTML block will not work pasted into JSX — use the code below instead.",
    steps: [
      "Save this as a component, e.g. ImmersiveWalkthrough.jsx.",
      "On Next.js with the App Router, add \"use client\" as the first line of the file.",
      "Render <ImmersiveWalkthrough /> wherever the walkthrough should appear.",
    ],
    caution: "Why it differs: JSX needs style as an object and allowFullScreen camel-cased, and a <script> tag written inside JSX never runs. Pasting the HTML version would still render the walkthrough, so nothing would look broken — the campaign tags would just silently stop arriving.",
  },
];

/* ── Derived state ──────────────────────────────────────────────────── */

export const placementAt = (
  placements: Placements,
  siteId: string,
  assetId: string,
): Placement | undefined => placements?.[siteId]?.[assetId];

/** Published = the tick. A placement with published:false keeps its key. */
export const isPublished = (
  placements: Placements,
  siteId: string,
  assetId: string,
): boolean => !!placementAt(placements, siteId, assetId)?.published;

export const sitesForAsset = (
  placements: Placements,
  sites: EmbedSite[],
  assetId: string,
): EmbedSite[] => sites.filter((s) => isPublished(placements, s.id, assetId));

export const assetsForSite = (
  placements: Placements,
  assets: EmbedAsset[],
  siteId: string,
): EmbedAsset[] => assets.filter((a) => isPublished(placements, siteId, a.id));

/** Total published pairs — the header's "N embeds in use". */
export const countLiveEmbeds = (
  placements: Placements,
  sites: EmbedSite[],
  assets: EmbedAsset[],
): number =>
  sites.reduce(
    (n, s) => n + assetsForSite(placements, assets, s.id).length,
    0,
  );

/** Assets published on at least one site — the sidebar's "3/4 live". */
export const countLiveAssets = (
  placements: Placements,
  sites: EmbedSite[],
  assets: EmbedAsset[],
): number =>
  assets.filter((a) => sitesForAsset(placements, sites, a.id).length > 0).length;

/**
 * Toggle a placement. Returns the next map plus the key in play, so the caller
 * can fire the matching server mutation.
 *
 * Unticking sets `published: false` and KEEPS the key — re-ticking restores the
 * identical code (§10.1.1). If unticking regenerated keys, one mis-click would
 * permanently kill a snippet already pasted on a customer's live page.
 */
export const togglePlacement = (
  placements: Placements,
  siteId: string,
  assetId: string,
): { next: Placements; key: string; published: boolean } => {
  const existing = placementAt(placements, siteId, assetId);
  const key = existing?.key || mintLocalKey();
  const published = !existing?.published;
  const next: Placements = {
    ...placements,
    [siteId]: {
      ...(placements[siteId] || {}),
      [assetId]: { key, published, local: existing ? existing.local : true },
    },
  };
  return { next, key, published };
};

/** Unpublish everything in the project — the footer's "Turn off all embeds". */
export const turnOffAll = (placements: Placements): Placements => {
  const next: Placements = {};
  Object.keys(placements || {}).forEach((siteId) => {
    next[siteId] = {};
    Object.keys(placements[siteId] || {}).forEach((assetId) => {
      next[siteId][assetId] = { ...placements[siteId][assetId], published: false };
    });
  });
  return next;
};

/**
 * Put back what turnOffAll switched off. A destructive-looking action needs a symmetric way
 * back: without this, one click costs an admin a manual re-tick per asset per website, and
 * the only reason they clicked was usually panic.
 *
 * Only placements that already exist are touched — this republishes, it never invents.
 */
export const turnAllOn = (placements: Placements): Placements => {
  const next: Placements = {};
  Object.keys(placements || {}).forEach((siteId) => {
    next[siteId] = {};
    Object.keys(placements[siteId] || {}).forEach((assetId) => {
      next[siteId][assetId] = { ...placements[siteId][assetId], published: true };
    });
  });
  return next;
};

/** Placements that exist but are switched off — what "Turn all back on" would restore. */
export const countOffEmbeds = (placements: Placements): number =>
  Object.keys(placements || {}).reduce(
    (n, siteId) =>
      n + Object.keys(placements[siteId] || {}).filter((a) => !placements[siteId][a].published).length,
    0,
  );

/**
 * Report a bulk result honestly.
 *
 * The old path reverted the WHOLE optimistic state when any one call failed, so the modal
 * showed every embed back on while some were genuinely off on the customer's site — the
 * modal confidently wrong about a live surface, which is the one thing it must never be.
 */
export const bulkOutcome = (total: number, failed: number, verb: string): string => {
  if (!failed) return "";
  if (failed >= total) return `Couldn't ${verb} any of them — nothing changed.`;
  return `${total - failed} of ${total} ${verb === "turn off" ? "turned off" : "turned back on"} — ${failed} failed.`;
};

/** Live embeds that a site-delete would break — drives the confirmation. */
export const liveEmbedsOnSite = (
  placements: Placements,
  assets: EmbedAsset[],
  siteId: string,
): number => assetsForSite(placements, assets, siteId).length;

/* ── Labels ─────────────────────────────────────────────────────────── */

/** `MM1-S1` — the human label for a placement. Never part of the URL (§7.1.2). */
export const placementLabel = (asset: EmbedAsset, site: EmbedSite): string =>
  `${asset.code}-${site.code}`;

export const siteSummary = (
  placements: Placements,
  assets: EmbedAsset[],
  site: EmbedSite,
): string => {
  const n = liveEmbedsOnSite(placements, assets, site.id);
  return [
    site.code,
    site.subs ? "+ all subdomains" : "apex + www only",
    n ? `${n} asset${n > 1 ? "s" : ""}` : "nothing embedded",
  ].join(" · ");
};

export const assetSiteBadge = (n: number): string =>
  n ? `${n} site${n > 1 ? "s" : ""}` : "Off";

/** Positional codes, assigned per kind: MM1, MM2, DT1… */
export const buildAssets = (
  metaModels: Array<{ id: string; name?: string; sub?: string }>,
  digitalTwins: Array<{ id: string; name?: string; sub?: string }>,
): EmbedAsset[] => [
  ...metaModels.map((m, i) => ({
    id: String(m.id),
    kind: "MM" as AssetKind,
    code: `MM${i + 1}`,
    name: String(m.name || "Untitled walkthrough"),
    sub: String(m.sub || ""),
  })),
  ...digitalTwins.map((d, i) => ({
    id: String(d.id),
    kind: "DT" as AssetKind,
    code: `DT${i + 1}`,
    name: String(d.name || "Digital twin"),
    sub: String(d.sub || ""),
  })),
];

/* ── Save-state classification ──────────────────────────────────────── */

export type SaveState = "idle" | "saving" | "unsaved" | "error";

/**
 * Is this failure "the server never answered" rather than a real rejection?
 *
 * Only an unreachable server keeps the optimistic state now. A server that DID
 * answer always reverts, because otherwise the UI is confidently wrong about a
 * customer's live site (§10.1.3).
 *
 * 404 USED TO MEAN "not shipped yet" and was swallowed. The endpoints landed on
 * 2026-09-01 (re-admin-server 66bb61c), so a 404 today is a real answer — an
 * unknown project or a revoked key — and swallowing it made the modal report a
 * save that never happened. That silent success is exactly what this must not
 * do, so 404 now reverts like any other server answer.
 */
export const isNotShipped = (err: any): boolean => {
  const status = err?.response?.status;
  if (status == null) return true; // network / CORS / no response
  return status === 501; // explicitly "not implemented", still not a real answer
};
